import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { translate, FACET_NONE, VOICE_THEME as t } from '../../utils/voiceMetadata';

/**
 * FilterPill — pill horizontal com dropdown popover (estilo ElevenLabs).
 *
 * Usado pros filtros com muitas opções (Idioma, Sotaque, Gênero). Os filtros
 * curtos (Categoria, Idade, Uso) continuam na sidebar lateral.
 *
 * Props:
 *   - icon: ReactNode (lucide icon)
 *   - label: string
 *   - group: { key, dict, counts } — vindo do FILTER_GROUPS do parent
 *   - activeSet: Set<string>
 *   - onToggle: (value) => void
 *   - isOpen: boolean — controlado pelo parent (apenas 1 pill aberta de cada vez)
 *   - onSetOpen: (open: boolean) => void
 *
 * O popover é renderizado via createPortal pra evitar problemas de stacking.
 */
const VISIBLE_LIMIT = 8;

export default function FilterPill({ icon, label, group, activeSet, onToggle, isOpen, onSetOpen }) {
  // Cores via CSS vars (VOICE_THEME) — adaptam ao tema sem useTheme
  const [expanded, setExpanded] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  const updateRect = useCallback(() => {
    if (!buttonRef.current) return;
    setButtonRect(buttonRef.current.getBoundingClientRect());
  }, []);

  // Quando abre: captura rect e adiciona listeners
  useEffect(() => {
    if (!isOpen) return undefined;
    updateRect();
    const onMouseDown = (e) => {
      const insideButton = buttonRef.current && buttonRef.current.contains(e.target);
      const insidePopover = popoverRef.current && popoverRef.current.contains(e.target);
      if (!insideButton && !insidePopover) onSetOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') onSetOpen(false); };
    // ITER4.4: scroll INTERNO no popover (scrollbar do menu) não fecha
    const onScroll = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      onSetOpen(false);
    };
    const onResize = () => updateRect();
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, onSetOpen, updateRect]);

  // Separa entradas normais vs "Outros"
  const allEntries = Object.entries(group.counts || {}).filter(([, count]) => count > 0);
  const normalEntries = allEntries
    .filter(([key]) => key !== FACET_NONE)
    .sort((a, b) => {
      const aActive = activeSet.has(a[0]);
      const bActive = activeSet.has(b[0]);
      if (aActive !== bActive) return aActive ? -1 : 1;
      return b[1] - a[1];
    });
  const noneEntry = allEntries.find(([key]) => key === FACET_NONE) || null;
  const hasMoreThanLimit = normalEntries.length > VISIBLE_LIMIT;
  const visibleNormal = (expanded || !hasMoreThanLimit)
    ? normalEntries
    : normalEntries.slice(0, VISIBLE_LIMIT);

  // Disable se não tem nenhuma entrada (raro, mas pode acontecer com filtros muito restritivos)
  const hasAny = normalEntries.length > 0 || !!noneEntry;

  const popoverStyles = buttonRect ? computePopoverPosition(buttonRect) : null;

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={buttonRef}
        onClick={() => onSetOpen(!isOpen)}
        disabled={!hasAny && activeSet.size === 0}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 11px',
          borderRadius: '9999px',
          border: isOpen
            ? '1px solid var(--dp-blue-500)'
            : activeSet.size > 0
              ? '1px solid rgba(13,174,255,0.45)'
              : '1px solid var(--dp-border-medium)',
          background: isOpen
            ? 'linear-gradient(135deg, rgba(13,174,255,0.16), rgba(13,174,255,0.06))'
            : activeSet.size > 0
              ? 'linear-gradient(135deg, rgba(13,174,255,0.10), rgba(13,174,255,0.02))'
              : 'transparent',
          color: activeSet.size > 0 ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
          fontSize: '11.5px',
          fontWeight: activeSet.size > 0 ? 600 : 500,
          fontFamily: 'var(--dp-font)',
          cursor: hasAny || activeSet.size > 0 ? 'pointer' : 'not-allowed',
          opacity: hasAny || activeSet.size > 0 ? 1 : 0.5,
          transition: 'all 0.18s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => { if (!isOpen && (hasAny || activeSet.size > 0)) e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = activeSet.size > 0
              ? 'rgba(13,174,255,0.45)'
              : 'var(--dp-border-medium)';
          }
        }}
      >
        {icon && <span style={{ display: 'inline-flex', color: '#06B6D4', flexShrink: 0 }}>{icon}</span>}
        <span>{label}</span>
        {activeSet.size > 0 && (
          <span style={{
            fontSize: '9.5px',
            fontWeight: 700,
            color: 'var(--dp-blue-500)',
            background: 'rgba(13,174,255,0.18)',
            padding: '1px 6px',
            borderRadius: '9999px',
            lineHeight: 1.4,
          }}>{activeSet.size}</span>
        )}
        <ChevronDown
          size={11}
          style={{
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            opacity: 0.7,
            flexShrink: 0,
          }}
        />
      </button>

      {isOpen && popoverStyles && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: `${popoverStyles.top}px`,
            left: `${popoverStyles.left}px`,
            width: `${popoverStyles.width}px`,
            maxHeight: '420px',
            overflowY: 'auto',
            background: t.bg,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid ${t.border}`,
            borderRadius: '10px',
            boxShadow: t.shadow,
            zIndex: 1300,
            padding: '6px 4px',
            fontFamily: 'var(--dp-font)',
            animation: 'agFadeInUp 0.15s ease-out',
            color: t.text,
          }}
          className="ag-scrollbar"
          onClick={(e) => e.stopPropagation()}
        >
          {visibleNormal.map(([value, count]) => (
            <Row
              key={value}
              label={translate(value, group.dict)}
              count={count}
              checked={activeSet.has(value)}
              onToggle={() => onToggle(value)}
            />
          ))}

          {hasMoreThanLimit && (
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{
                margin: '2px 4px',
                padding: '5px 8px',
                background: 'transparent',
                border: 'none',
                color: 'var(--dp-blue-500)',
                cursor: 'pointer',
                fontSize: '10.5px',
                fontWeight: 600,
                fontFamily: 'var(--dp-font)',
                textAlign: 'left',
                letterSpacing: '0.2px',
                width: 'calc(100% - 8px)',
              }}
            >
              {expanded ? '− Ver menos' : `+ Ver todos (${normalEntries.length})`}
            </button>
          )}

          {noneEntry && (
            <>
              <div style={{
                height: '1px',
                background: 'var(--dp-border-subtle)',
                margin: '4px 6px',
                opacity: 0.5,
              }} />
              <Row
                label="Outros"
                count={noneEntry[1]}
                checked={activeSet.has(FACET_NONE)}
                onToggle={() => onToggle(FACET_NONE)}
                muted
                title="Vozes sem metadados (CapCut, Multivoz, etc)"
              />
            </>
          )}

          {!hasAny && (
            <div style={{
              padding: '12px 10px',
              fontSize: '11px',
              color: 'var(--dp-text-tertiary)',
              fontStyle: 'italic',
            }}>
              Nenhum valor disponível com os filtros atuais.
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

function Row({ label, count, checked, onToggle, muted = false, title }) {
  const accentColor = muted ? '#94A3B8' : 'var(--dp-blue-500)';
  const baseColor = muted ? 'var(--dp-text-tertiary)' : 'var(--dp-text-secondary)';
  const activeBg = muted ? 'rgba(148,163,184,0.10)' : 'rgba(13,174,255,0.10)';
  return (
    <label
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        borderRadius: '6px',
        cursor: 'pointer',
        background: checked ? activeBg : 'transparent',
        transition: 'background 0.15s',
        fontSize: '11.5px',
        color: checked ? 'var(--dp-text-primary)' : baseColor,
        fontStyle: muted ? 'italic' : 'normal',
      }}
      onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = t.hover; }}
      onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        style={{ width: '13px', height: '13px', accentColor, cursor: 'pointer', flexShrink: 0 }}
      />
      <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      <span style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', flexShrink: 0 }}>{count}</span>
    </label>
  );
}

function computePopoverPosition(rect) {
  const PAD = 8;
  const GAP = 4;
  const MIN_WIDTH = 240;
  const MAX_WIDTH = 320;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let width = Math.min(MAX_WIDTH, Math.max(rect.width, MIN_WIDTH));
  let left = rect.left;
  if (left + width + PAD > viewportW) {
    left = Math.max(PAD, rect.right - width);
  }
  if (left < PAD) left = PAD;

  let top = rect.bottom + GAP;
  const spaceBelow = viewportH - rect.bottom - PAD;
  if (spaceBelow < 200 && rect.top > 200) {
    top = Math.max(PAD, rect.top - GAP - 400);
  }
  return { top, left, width };
}
